/**
 * Recipe Notes Service
 * Handles recipe notes updates (notes are stored in recipes.notes column)
 */

import { BaseApiService } from './BaseApiService'

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:3005'

interface UpdateNotesResponse {
  success: boolean
  message: string
  data: {
    id: string
    notes: string | null
    [key: string]: any
  }
}

class RecipeNotesService extends BaseApiService {
  constructor() {
    super({
      baseUrl: API_BASE_URL,
      defaultTimeout: 10000,
      defaultRetries: 3,
      enableRetry: true
    })
  }

  /**
   * Update notes for a recipe
   * @param recipeId - Recipe UUID
   * @param notes - Notes text (max 2000 characters, empty string to clear)
   * @returns The updated recipe record
   */
  async updateNotes(recipeId: string, notes: string): Promise<string | null> {
    const data = await this.patch<UpdateNotesResponse>(
      `/api/recipes/${recipeId}/notes`,
      { notes: notes.trim() || null }
    )
    return data.data.notes
  }

  /**
   * Get notes for a recipe (included in recipe fetch)
   * This is a placeholder - notes are fetched with the recipe record
   * @param recipeId - Recipe UUID
   * @returns Notes text or null
   */
  async getNotes(_recipeId: string): Promise<string | null> {
    // Notes are included in the recipe record from GET /api/users/:userId/recipes
    // This method is kept for backward compatibility but should not be used directly
    throw new Error('getNotes is deprecated - notes are included in recipe record')
  }
}

// Export singleton instance
export const recipeNotesService = new RecipeNotesService()

// Also export the class for testing
export default RecipeNotesService
